import { BadgePercent, Plus } from "lucide-react";
import { toast } from "sonner";
import { eur } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useDiscounts, tierRate, fmtRate } from "@/lib/useDiscounts";

// Grille tarifaire dégressive sur la fiche produit : une ligne par palier (quantité, prix unitaire, remise)
// + ajout direct de la quantité du palier à la commande. Cumulable entre les vins du même viticulteur.
export default function TierPricing({ wine }) {
  const tiersMap = useDiscounts();
  const { add, items } = useCart();
  const tiers = tiersMap[wine.producer];
  if (!tiers || !tiers.length) return null;

  // quantité panachée actuelle de ce viticulteur dans la commande -> palier actif
  const mixQty = items.filter((i) => i.wine.producer === wine.producer).reduce((s, i) => s + i.qty, 0);
  const activeRate = tierRate(tiers, mixQty);

  const rows = [{ threshold: 1, rate: 0 }, ...tiers];

  const quickAdd = async (qty) => {
    await add(wine, qty);
    toast.success(`${qty} bouteille${qty > 1 ? "s" : ""} ajoutée${qty > 1 ? "s" : ""} à votre commande`);
  };

  return (
    <div className="mt-4 bg-[#F5EEDC] border border-[#C5A059]/40 rounded-2xl px-4 py-4" data-testid="tier-pricing">
      <p className="flex items-center gap-2 text-xs font-bold text-[#8C6D23]">
        <BadgePercent size={15} className="shrink-0" />
        Prix dégressifs — Offre {wine.producer}
      </p>
      <p className="text-[0.65rem] text-[#57534E] mt-0.5 mb-3">Cumulable entre les vins de ce viticulteur.</p>
      <div className="divide-y divide-[#C5A059]/20">
        {rows.map((t) => {
          const unit = wine.price - t.rate;
          const active = t.rate === activeRate && (t.rate > 0 || mixQty === 0);
          return (
            <div
              key={t.threshold}
              className={`flex items-center justify-between gap-3 py-2 ${active && t.rate > 0 ? "bg-[#6B1226]/5 -mx-2 px-2 rounded-xl" : ""}`}
              data-testid={`tier-row-${t.threshold}`}
            >
              <div className="min-w-[4.5rem]">
                <p className="text-sm font-semibold text-[#1C1917]">
                  {t.threshold === 1 ? "À l'unité" : `Par ${t.threshold}`}
                </p>
                {t.rate > 0 && <p className="text-[0.6rem] font-semibold text-[#8C6D23]">{fmtRate(t.rate)} la bouteille</p>}
              </div>
              <p className={`text-sm font-semibold ${t.rate > 0 ? "text-[#6B1226]" : "text-[#1C1917]"}`}>
                {eur(unit)} <span className="text-[0.6rem] font-normal text-[#57534E]">/bt</span>
              </p>
              <button
                onClick={() => quickAdd(t.threshold)}
                className="inline-flex items-center gap-1 rounded-full border border-[#6B1226]/30 text-[#6B1226] text-[0.65rem] font-semibold px-3 py-1.5 hover:bg-[#6B1226] hover:text-[#FAF6F0] transition-colors shrink-0"
                data-testid={`tier-add-${t.threshold}`}
                aria-label={`Commander ${t.threshold === 1 ? "une bouteille" : `${t.threshold} bouteilles`} à ${eur(unit)} l'unité`}
              >
                <Plus size={12} /> {t.threshold === 1 ? "1 bt" : `${t.threshold} bt`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
